import { BusyContent } from '../ui/Primitives';
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { getBackendHost } from '../../utils/backendHost';
import { SUPPORTED_TIMEZONES } from '../../utils/timezones';
import './SchedulePicker.css';

const DAYS = [
  { key: 'mon', label: 'Monday' }, { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' }, { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' }, { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];
const LEAD_TIMES = [1, 2, 3, 4, 5, 6, 7, 14, 30, 60];
const DURATIONS = [15, 30, 45, 60, 75, 90, 105, 120];

function emptyGrid() {
  return { sun: [], mon: [], tue: [], wed: [], thu: [], fri: [], sat: [] };
}

function normalizedGrid(value) {
  let parsed = value;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch (_error) { parsed = {}; }
  }
  return DAYS.reduce((grid, day) => {
    grid[day.key] = Array.isArray(parsed?.[day.key]) ? [...parsed[day.key]] : [];
    return grid;
  }, emptyGrid());
}

function timeRangeParts(value) {
  const match = String(value || '').match(/^\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*-\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*$/i);
  if (!match) return { start: '09:00', end: '17:00' };
  const to24Hour = (hourValue, minuteValue, meridiemValue) => {
    let hour = Number(hourValue) % 12;
    if (meridiemValue.toUpperCase() === 'PM') hour += 12;
    return `${String(hour).padStart(2, '0')}:${String(Number(minuteValue || 0)).padStart(2, '0')}`;
  };
  return {
    start: to24Hour(match[1], match[2], match[3]),
    end: to24Hour(match[4], match[5], match[6]),
  };
}

function storedTime(value) {
  const [rawHour, rawMinute] = String(value || '09:00').split(':');
  const hour = Number(rawHour);
  const minute = String(Number(rawMinute || 0)).padStart(2, '0');
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${minute}${meridiem}`;
}

function storedRange(start, end) {
  return `${storedTime(start)} - ${storedTime(end)}`;
}

function gridFromInterpretation(items) {
  const next = emptyGrid();
  (items || []).forEach(item => {
    const day = String(item.day || '').slice(0, 3).toLowerCase();
    if (next[day] && item.time_range && !next[day].includes(item.time_range)) {
      next[day].push(item.time_range);
    }
  });
  return next;
}

function durationLabel(value) {
  if (Number(value) < 60) return `${value} minutes`;
  const hours = Number(value) / 60;
  return Number.isInteger(hours) ? `${hours} hour${hours === 1 ? '' : 's'}` : `${value} minutes`;
}

function SchedulePicker({ organization, onSave, onClose }) {
  const [source, setSource] = useState(organization?.business_hours ? 'business' : 'describe');
  const [description, setDescription] = useState('');
  const [availabilityGrid, setAvailabilityGrid] = useState(() => normalizedGrid(organization?.availability));
  const [selectedTimezone, setSelectedTimezone] = useState(organization?.timezone || 'America/Los_Angeles');
  const [selectedDuration, setSelectedDuration] = useState(String(organization?.duration || 30));
  const [selectedLeadTime, setSelectedLeadTime] = useState(String(organization?.lead_time ?? 1));
  const [phase, setPhase] = useState('idle');
  const [summary, setSummary] = useState('');
  const [clarification, setClarification] = useState('');
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [clearedSchedule, setClearedSchedule] = useState(null);
  const hasAvailability = useMemo(() => DAYS.some(day => availabilityGrid[day.key].length), [availabilityGrid]);

  const markChanged = () => {
    setDirty(true);
  };

  const chooseSource = nextSource => {
    setSource(nextSource);
    setClarification('');
    setError('');
  };

  const buildSchedule = async () => {
    const query = source === 'business'
      ? `Use these regular business hours for appointments: ${organization.business_hours}`
      : description.trim();
    if (!query) return setError('Describe the days and times customers can book.');
    setPhase('interpreting');
    setError('');
    setClarification('');
    try {
      const response = await axios.post(`${getBackendHost()}/time-range`, {
        jwt_token: localStorage.getItem('jwt_authorization'), user_query: query, preview_only: true,
      });
      if (response.data.error) throw new Error(response.data.error);
      if (response.data.status === 'clarification') {
        setClarification(response.data.question);
        setPhase('clarification');
        return;
      }
      const nextGrid = gridFromInterpretation(response.data.availability || response.data);
      if (!DAYS.some(day => nextGrid[day.key].length)) {
        throw new Error('No appointment hours were found. Include the days and times customers can book.');
      }
      setAvailabilityGrid(nextGrid);
      setClearedSchedule(null);
      setSummary(response.data.summary || 'Review the schedule below before saving it.');
      setDirty(true);
      setPhase('review');
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'We could not build that schedule.');
      setPhase('idle');
    }
  };

  const toggleDay = dayKey => {
    setAvailabilityGrid(current => ({ ...current, [dayKey]: current[dayKey].length ? [] : ['9:00AM - 5:00PM'] }));
    setClearedSchedule(null);
    markChanged();
  };

  const updateRange = (dayKey, index, field, value) => {
    setAvailabilityGrid(current => ({
      ...current,
      [dayKey]: current[dayKey].map((range, rangeIndex) => {
        if (rangeIndex !== index) return range;
        const parts = timeRangeParts(range);
        return storedRange(field === 'start' ? value : parts.start, field === 'end' ? value : parts.end);
      }),
    }));
    setClearedSchedule(null);
    markChanged();
  };

  const addRange = dayKey => {
    setAvailabilityGrid(current => ({ ...current, [dayKey]: [...current[dayKey], '9:00AM - 5:00PM'] }));
    setClearedSchedule(null);
    markChanged();
  };

  const removeRange = (dayKey, index) => {
    setAvailabilityGrid(current => ({ ...current, [dayKey]: current[dayKey].filter((_range, rangeIndex) => rangeIndex !== index) }));
    setClearedSchedule(null);
    markChanged();
  };

  const clearWeek = () => {
    if (!hasAvailability) return;
    setClearedSchedule(normalizedGrid(availabilityGrid));
    setAvailabilityGrid(emptyGrid());
    setSummary('No appointment times will be offered until you add availability again.');
    setError('');
    setDirty(true);
    setPhase('review');
  };

  const undoClearWeek = () => {
    if (!clearedSchedule) return;
    setAvailabilityGrid(normalizedGrid(clearedSchedule));
    setClearedSchedule(null);
    setSummary('Your previous appointment hours have been restored.');
    setError('');
    setDirty(true);
    setPhase('review');
  };

  const saveSchedule = async () => {
    const invalidRange = DAYS.some(day => availabilityGrid[day.key].some(range => {
      const { start, end } = timeRangeParts(range);
      return start >= end;
    }));
    if (invalidRange) return setError('Each ending time must be later than its starting time.');
    setPhase('saving');
    setError('');
    try {
      await onSave({
        availability: JSON.stringify(availabilityGrid), timezone: selectedTimezone,
        duration: Number(selectedDuration), leadTime: Number(selectedLeadTime),
      });
      setDirty(false);
      setClearedSchedule(null);
      onClose();
    } catch (saveError) {
      setError(saveError.response?.data?.error?.message || 'Your schedule could not be saved.');
      setPhase('review');
    }
  };

  const settingChanged = setter => event => {
    setter(event.target.value);
    markChanged();
  };

  return (
    <div className="schedule-builder">
      <div className="schedule-builder-intro">
        <span className="schedule-builder-step">1</span>
        <div><h3>When can customers book?</h3><p>Start with your regular hours, or describe a different appointment schedule in your own words.</p></div>
      </div>

      <div className="schedule-source-options" role="radiogroup" aria-label="Appointment schedule source">
        <button type="button" role="radio" aria-checked={source === 'business'} className={`schedule-source-option ${source === 'business' ? 'selected' : ''}`} onClick={() => chooseSource('business')} disabled={!organization?.business_hours || phase === 'interpreting'}>
          <span className="schedule-source-check" aria-hidden="true" />
          <span><strong>Use regular business hours</strong><small>{organization?.business_hours || 'Add business hours in Knowledge first.'}</small></span>
        </button>
        <button type="button" role="radio" aria-checked={source === 'describe'} className={`schedule-source-option ${source === 'describe' ? 'selected' : ''}`} onClick={() => chooseSource('describe')} disabled={phase === 'interpreting'}>
          <span className="schedule-source-check" aria-hidden="true" />
          <span><strong>Set different appointment hours</strong><small>Tell Sayable what works, then review the result.</small></span>
        </button>
      </div>

      {source === 'describe' ? (
        <div className="schedule-description-field">
          <label htmlFor="appointment-hours-description">Describe when appointments are available</label>
          <textarea id="appointment-hours-description" data-say-id="schedule-picker-gpt-input" value={description} onChange={event => { setDescription(event.target.value); setClarification(''); setError(''); }} placeholder="Example: Weekdays from 9 to 5, except lunch from noon to 1. Saturdays from 10 to 2." rows="3" disabled={phase === 'interpreting'} />
          {clarification ? <div className="schedule-clarification" role="status"><strong>One quick detail</strong>{clarification}</div> : null}
        </div>
      ) : null}

      <button type="button" data-say-id="schedule-picker-gpt-submit" className="schedule-build-button" onClick={buildSchedule} disabled={phase === 'interpreting' || (source === 'describe' && !description.trim())}>
        <BusyContent busy={phase === 'interpreting'} label={"Building your schedule…"}>{'Build my schedule'}</BusyContent>
      </button>

      {phase === 'interpreting' ? (
        <div className="schedule-thinking" role="status" aria-live="polite">
          <span className="say-loading-description">Building your appointment hours…</span>
          <div className="schedule-thinking-lines" aria-hidden="true"><i /><i /><i /></div>
        </div>
      ) : null}

      <div className={`schedule-review ${phase === 'interpreting' ? 'is-muted' : ''}`}>
        <div className="schedule-builder-intro schedule-review-heading">
          <span className="schedule-builder-step">2</span>
          <div><h3>Review your week</h3><p>{summary || 'Turn days on or off and adjust any time before saving.'}</p></div>
          <button type="button" className="schedule-clear-week" onClick={clearWeek} disabled={!hasAvailability || phase === 'interpreting' || phase === 'saving'}>Clear all</button>
        </div>
        <div className="schedule-week">
          {DAYS.map((day, dayIndex) => {
            const ranges = availabilityGrid[day.key];
            const active = Boolean(ranges.length);
            return (
              <div className={`schedule-day-row ${active ? 'is-available' : ''}`} key={day.key} style={{ '--schedule-row-index': dayIndex }}>
                <label className="schedule-day-toggle"><input type="checkbox" checked={active} onChange={() => toggleDay(day.key)} /><span aria-hidden="true" /><strong>{day.label}</strong></label>
                <div className="schedule-day-times">
                  {!active ? <span className="schedule-unavailable">Not available</span> : ranges.map((range, index) => {
                    const parts = timeRangeParts(range);
                    return (
                      <div className="schedule-time-range" key={`${day.key}-${index}`}>
                        <input aria-label={`${day.label} start time`} type="time" value={parts.start} onChange={event => updateRange(day.key, index, 'start', event.target.value)} />
                        <span>to</span>
                        <input aria-label={`${day.label} end time`} type="time" value={parts.end} onChange={event => updateRange(day.key, index, 'end', event.target.value)} />
                        <button type="button" className="schedule-remove-range" onClick={() => removeRange(day.key, index)} aria-label={`Remove ${day.label} time range`}>×</button>
                      </div>
                    );
                  })}
                  {active && ranges.length < 3 ? <button type="button" className="schedule-add-range" onClick={() => addRange(day.key)}>+ Add another time</button> : null}
                </div>
              </div>
            );
          })}
        </div>
        {!hasAvailability ? (
          <div className="schedule-empty-week" role="status">
            <div><strong>No appointment times selected</strong><span>Sayable will not offer appointments. Save this draft, then publish when you want the change to take effect.</span></div>
            {clearedSchedule ? <button type="button" onClick={undoClearWeek}>Undo clear</button> : null}
          </div>
        ) : null}
      </div>

      <div className="schedule-details">
        <div className="schedule-builder-intro schedule-details-heading"><span className="schedule-builder-step">3</span><div><h3>Booking details</h3><p>These settings help Sayable offer the right appointment times.</p></div></div>
        <div className="schedule-detail-grid">
          <label>Timezone<select data-say-id="schedule-timezone-select" value={selectedTimezone} onChange={settingChanged(setSelectedTimezone)}>{SUPPORTED_TIMEZONES.map(timezone => <option key={timezone.value} value={timezone.value}>{timezone.label}</option>)}</select></label>
          <label>Advance notice<select data-say-id="schedule-lead-time-select" value={selectedLeadTime} onChange={settingChanged(setSelectedLeadTime)}>{LEAD_TIMES.map(day => <option key={day} value={day}>{day} day{day === 1 ? '' : 's'}</option>)}</select></label>
          <label>Meeting length<select data-say-id="schedule-duration-select" value={selectedDuration} onChange={settingChanged(setSelectedDuration)}>{DURATIONS.map(duration => <option key={duration} value={duration}>{durationLabel(duration)}</option>)}</select></label>
        </div>
      </div>

      {error ? <div className="schedule-builder-error" role="alert">{error}</div> : null}
      <div className="schedule-builder-actions">
        <button type="button" className="schedule-cancel-button" onClick={onClose}>Cancel</button>
        <button type="button" className="schedule-save-button" onClick={saveSchedule} disabled={!dirty || phase === 'saving' || phase === 'interpreting'}><BusyContent busy={phase === 'saving'} label={"Saving schedule…"}>{'Use this schedule'}</BusyContent></button>
      </div>
    </div>
  );
}

export default SchedulePicker;
